import { useCallback, useEffect, useState } from "react";

import {
  LogLine,
  ObservabilityPipelineLogs,
  ObservabilityPipelineLogsOverview
} from "Api/observability/pipeline/types";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import useObservabilityPipelineHttpClient from "src/organization/pages/project/environment/observabilitySandbox/hooks/useObservabilityPipelineHttpClient";

import { FilterSelectValueMap } from "../components/overview/FilterSelect/types";

const useLogs = (timeFrame: TimeFrameType) => {
  const obsPipelineHttpClient = useObservabilityPipelineHttpClient();

  const [isUpdating, onLoadingChange] = useState(true);
  const [isSkeletonVisible, setShowSkeleton] = useState(true);

  const [logsOverview, setLogsOverview] =
    useState<ObservabilityPipelineLogsOverview | null>(null);
  const [logsGenerator, setLogsGenerator] =
    useState<AsyncGenerator<ObservabilityPipelineLogs> | null>(null);

  const [logs, setLogs] = useState<LogLine[] | null>(null);
  const [loadingMoreLogs, setLoadingMoreLogs] = useState<boolean>(false);

  const [selection, setSelection] = useState<FilterSelectValueMap>({});

  const handleFiltersChange = (fsvm: FilterSelectValueMap) => {
    setSelection(fsvm);
  };

  const handleLoadMore = useCallback(() => {
    async function fetchNextLogs() {
      if (!logsGenerator) {
        return;
      }

      setLoadingMoreLogs(true);

      try {
        const logsResponse = await logsGenerator.next();
        setLogs(prevLogs => [
          ...(prevLogs || []),
          ...(logsResponse.value?.data || [])
        ]);
      } finally {
        setLoadingMoreLogs(false);
      }
    }

    fetchNextLogs();
  }, [logsGenerator]);

  useEffect(() => {
    async function fetchLogsOverview() {
      if (obsPipelineHttpClient === null) {
        return;
      }

      try {
        onLoadingChange(true);
        const responseOverview = await obsPipelineHttpClient.getLogsOverview(
          timeFrame,
          selection
        );

        const logsGenerator = obsPipelineHttpClient.queryLogs(
          timeFrame,
          selection
        );

        onLoadingChange(false);
        setLogsOverview(responseOverview);
        setLogsGenerator(logsGenerator);
      } catch {
        onLoadingChange(false);
      }
    }

    fetchLogsOverview();
  }, [obsPipelineHttpClient, timeFrame, selection]);

  useEffect(() => {
    if (!isUpdating && logsGenerator !== undefined) {
      setShowSkeleton(false);
    }
  }, [isUpdating, logsGenerator]);

  useEffect(() => {
    setShowSkeleton(true);
  }, [timeFrame]);

  useEffect(() => {
    async function fetchNextLogs() {
      if (!logsGenerator) {
        return;
      }

      const logsResponse = await logsGenerator.next();
      setLogs(logsResponse.value?.data || []);
    }

    fetchNextLogs();
  }, [logsGenerator]);

  return {
    data: {
      logsOverview,
      logs,
      selection
    },
    actions: {
      handleLoadMore,
      handleFiltersChange
    },
    status: {
      loadingMoreLogs,
      isSkeletonVisible
    }
  };
};

export default useLogs;
